import {
    CSSProperties,
    ChangeEvent,
    FC,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    BodyText1,
    Button,
    Dropdown,
    FieldLabel,
    FlexColumn,
    FlexRow,
    Tooltip,
} from '@portal/common/components';
import { MEASURE_ID_KEY, ROUND_ID_KEY } from '@portal/common/models/data-key';
import { styled } from '@portal/common/theme';

import config from '../../../config';
import _ from '../../../locale';
import { useRaceEthnicityAgeOptionsRestriction } from './use-race-ethnicity-age-options-restriction';

const YearDropdown = styled(Dropdown)({
    width: 184,
});

const YearsDivider = styled(BodyText1)(({ theme }) => ({
    paddingTop: 42,
    color: theme.color.gray40,
}));

const AddBtn = styled(Button)({
    marginTop: 30,
    minWidth: 0,
    width: 68,
});

const restrictedOptionTooltipTriggerStyles: CSSProperties = {
    display: 'block',
    width: '100%',
};

const ModalStackTooltip = styled(Tooltip)(({ theme }) => ({
    zIndex: theme.zIndex.modal + 1,
}));

const sortRange = (value: number[]): number[] => value.sort((a, b) => a - b);

export const NO_BOUND_VALUE = 'null';

interface CustomAgeGroupSelectorProps {
    isSingleRangeReport?: boolean;
    supportedAgeRange: {
        min: number;
        max: number;
    };
    selectedMetrics: number[];
    selectedFilters: Record<string, any>;
    onChange: (range: (number | string)[]) => void;
    onSave: (range: (number | string)[]) => void;
    dataProjectId?: number;
    onValidityChange?: (isValid: boolean) => void;
}

const CustomAgeGroupSelector: FC<CustomAgeGroupSelectorProps> = ({
    isSingleRangeReport,
    supportedAgeRange: { min, max },
    selectedMetrics,
    selectedFilters,
    onChange,
    onSave,
    dataProjectId,
    onValidityChange,
}) => {
    const [startValue, setStartValue] = useState<number>(min);
    const [endValue, setEndValue] = useState<number | string>(max);

    const selectedMeasures = useMemo(
        () => (selectedFilters?.[MEASURE_ID_KEY] as number[]) || [],
        [selectedFilters]
    );

    const {
        hasRestrictedAgeRanges,
        restrictedAgeRangesMessage,
        isRestrictedStartAge,
        isRestrictedEndAge,
        isRestrictedBand,
    } = useRaceEthnicityAgeOptionsRestriction({
        dataProjectId,
        selectedMeasures,
        isSingleAgesReport: isSingleRangeReport,
    });

    const areNoBoundsAllowed = useMemo(() => {
        const hasOnlyRateMetric =
            selectedMetrics?.length === 1 && selectedMetrics.includes(config.rateMetricId);
        const isGbd2023Round =
            (selectedFilters?.[ROUND_ID_KEY] as number[])?.[0] === config.gbd2023RoundId;

        if (isSingleRangeReport || (hasOnlyRateMetric && isGbd2023Round)) {
            return false;
        }

        return true;
    }, [isSingleRangeReport, selectedMetrics, selectedFilters]);

    const startYearOptions = useMemo(() => {
        const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);

        // Add 0 option at the beginning
        if (areNoBoundsAllowed && min > 0) {
            options.unshift(0);
        }
        return options;
    }, [min, max, areNoBoundsAllowed]);

    const endYearOptions = useMemo(() => {
        const options: (number | string)[] = Array.from(
            { length: max - min + 1 },
            (_, i) => min + i
        );
        if (areNoBoundsAllowed) {
            options.push(NO_BOUND_VALUE);
        }
        return options;
    }, [min, max, areNoBoundsAllowed]);

    const isCurrentSelectionValid = useMemo(
        () => !isRestrictedBand(startValue, endValue),
        [isRestrictedBand, startValue, endValue]
    );

    useEffect(() => {
        onValidityChange?.(isCurrentSelectionValid);
    }, [isCurrentSelectionValid, onValidityChange]);

    const isStartAgeDisabled = useCallback(
        (age: number) => {
            if (!isSingleRangeReport && age === endValue) {
                return true;
            }
            if (isRestrictedStartAge(age)) {
                return true;
            }
            if (endValue === NO_BOUND_VALUE) {
                return false;
            }
            const [sortedStart, sortedEnd] = sortRange([age, endValue as number]);
            return isRestrictedBand(sortedStart, sortedEnd);
        },
        [isSingleRangeReport, endValue, isRestrictedStartAge, isRestrictedBand]
    );

    const isEndAgeDisabled = useCallback(
        (age: number | string) => {
            if (!isSingleRangeReport && age === startValue) {
                return true;
            }
            if (isRestrictedEndAge(age, startValue)) {
                return true;
            }
            if (age === NO_BOUND_VALUE) {
                return false;
            }
            const [sortedStart, sortedEnd] = sortRange([startValue, age as number]);
            return isRestrictedBand(sortedStart, sortedEnd);
        },
        [isSingleRangeReport, startValue, isRestrictedEndAge, isRestrictedBand]
    );

    const setRangeValues = useCallback((start: number, end: number | string) => {
        if (end === NO_BOUND_VALUE) {
            setStartValue(start);
            setEndValue(end);
        } else {
            const [sortedStart, sortedEnd] = sortRange([start, end as number]);
            setStartValue(sortedStart);
            setEndValue(sortedEnd);
        }
    }, []);

    const handleStartChange = useCallback(
        (ev: ChangeEvent<HTMLSelectElement>) => {
            const value = Number(ev.target.value);
            setRangeValues(value, endValue);
        },
        [endValue, setRangeValues]
    );

    const handleEndChange = useCallback(
        (ev: ChangeEvent<HTMLSelectElement>) => {
            const value =
                ev.target.value === NO_BOUND_VALUE ? NO_BOUND_VALUE : Number(ev.target.value);
            setRangeValues(startValue, value);
        },
        [startValue, setRangeValues]
    );

    const customAgeLabelGetter = useCallback(
        (o) => (o === NO_BOUND_VALUE ? `${max + 1}+` : String(o)),
        [max]
    );

    const handleAddClick = useCallback(() => {
        if (endValue === NO_BOUND_VALUE) {
            onSave([startValue, endValue]);
        } else {
            onSave(sortRange([startValue, endValue as number]));
        }
    }, [startValue, endValue, onSave]);

    const renderStartOption = useCallback(
        (option: number, optionGetters: { optionLabelGetter: (o: number) => string }) => {
            const label = optionGetters.optionLabelGetter(option);
            if (isRestrictedStartAge(option)) {
                return (
                    <ModalStackTooltip
                        title={restrictedAgeRangesMessage}
                        placement="right"
                        wrapperStyles={restrictedOptionTooltipTriggerStyles}
                    >
                        {label}
                    </ModalStackTooltip>
                );
            }
            return label;
        },
        [isRestrictedStartAge, restrictedAgeRangesMessage]
    );

    const renderEndOption = useCallback(
        (
            option: number | string,
            optionGetters: { optionLabelGetter: (o: number | string) => string }
        ) => {
            const label = optionGetters.optionLabelGetter(option);
            if (isRestrictedEndAge(option, startValue)) {
                return (
                    <ModalStackTooltip
                        title={restrictedAgeRangesMessage}
                        placement="right"
                        wrapperStyles={restrictedOptionTooltipTriggerStyles}
                    >
                        {label}
                    </ModalStackTooltip>
                );
            }
            return label;
        },
        [isRestrictedEndAge, startValue, restrictedAgeRangesMessage]
    );

    useEffect(() => {
        if (endValue === NO_BOUND_VALUE) {
            onChange([startValue, endValue]);
        } else {
            onChange(sortRange([startValue, endValue as number]));
        }
    }, [startValue, endValue, onChange]);

    useEffect(() => {
        if (isSingleRangeReport) {
            const newStartValue = startValue < min ? min : startValue;
            const newEndValue = endValue === NO_BOUND_VALUE ? max : endValue;
            setRangeValues(newStartValue, newEndValue);
        }
    }, [isSingleRangeReport]);

    return (
        <>
            <FieldLabel>{_('export_single_age_range_slider_title')}</FieldLabel>
            <FlexRow itemsSpacing={12}>
                <FlexColumn itemsSpacing={12}>
                    <FieldLabel>{_('export_single_report_start_label')}</FieldLabel>
                    <YearDropdown
                        id="age-start"
                        aria-label={_('export_single_report_start_label')}
                        options={startYearOptions}
                        valueMode="single"
                        value={startValue}
                        onChange={handleStartChange}
                        optionDisabledGetter={isStartAgeDisabled}
                        renderOption={hasRestrictedAgeRanges ? renderStartOption : undefined}
                    />
                </FlexColumn>
                <YearsDivider>-</YearsDivider>
                <FlexColumn itemsSpacing={12}>
                    <FieldLabel>{_('export_single_report_end_label')}</FieldLabel>
                    <YearDropdown
                        options={endYearOptions}
                        valueMode="single"
                        value={endValue}
                        onChange={handleEndChange}
                        optionDisabledGetter={isEndAgeDisabled}
                        optionLabelGetter={customAgeLabelGetter}
                        renderOption={hasRestrictedAgeRanges ? renderEndOption : undefined}
                    />
                </FlexColumn>
                {!isSingleRangeReport && (
                    <AddBtn onClick={handleAddClick} disabled={!isCurrentSelectionValid}>
                        {_('export_single_report_add_another_aggregate_btn')}
                    </AddBtn>
                )}
            </FlexRow>
        </>
    );
};

export default memo(CustomAgeGroupSelector);
